// VibeTagFilter Component - Horizontale Chip-Leiste fuer Vibe-Tags (Realm-aware)

import React, { useRef, useState, useEffect } from 'react';
import {
  UserCircle,
  MessageCircle,
  Sparkles,
  DoorOpen,
  Hammer,
  History,
  Ear,
  Coffee,
  Lightbulb,
  X,
  ChevronLeft,
  ChevronRight,
  LucideIcon,
  // GoodWork Icons
  Focus,
  Users,
  Clock,
  Briefcase,
  Heart,
  Rocket,
  Leaf,
} from 'lucide-react';
import { VIBE_TAGS, GOODWORK_VIBE_TAGS, VibeTag, GoodWorkVibeTag } from '../../types';
import { useRealm } from '../../context/RealmContext';

// Icon mapping for GoodLife vibe tags (philosophische Tags nach Harald Welzer)
const GOODLIFE_ICON_MAP: Record<string, LucideIcon> = {
  UserCircle,      // rollenlos
  MessageCircle,   // schnack-potential
  Sparkles,        // unverfuegbares-glueck
  DoorOpen,        // offen-fuer-alle
  Hammer,          // selbstwirksam
  History,         // antiquiert-und-gut
  Ear,             // empfangsbereit
  Coffee,          // anlasslos
  Lightbulb,       // selbstueberraschung
};

// Icon mapping for GoodWork vibe tags
const GOODWORK_ICON_MAP: Record<string, LucideIcon> = {
  Focus,           // fokussiert
  Users,           // kollaborativ
  Sparkles,        // inspirierend
  Clock,           // flexibel
  Briefcase,       // professionell
  Heart,           // community
  Rocket,          // gruendertauglich
  Leaf,            // nachhaltig
};

interface VibeTagFilterProps {
  // GoodLife tags
  selectedTags: VibeTag[];
  onTagToggle: (tag: VibeTag) => void;
  // GoodWork tags (optional - fuer Realm-aware)
  selectedGoodworkTags?: GoodWorkVibeTag[];
  onGoodworkTagToggle?: (tag: GoodWorkVibeTag) => void;
  onClearAll?: () => void;
}

export function VibeTagFilter({
  selectedTags,
  onTagToggle,
  selectedGoodworkTags = [],
  onGoodworkTagToggle,
  onClearAll,
}: VibeTagFilterProps) {
  const { isGoodWork } = useRealm();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // Waehle Tags und Handler basierend auf Realm
  const tags = isGoodWork ? GOODWORK_VIBE_TAGS : VIBE_TAGS;
  const iconMap = isGoodWork ? GOODWORK_ICON_MAP : GOODLIFE_ICON_MAP;
  const currentSelectedTags = isGoodWork ? selectedGoodworkTags : selectedTags;
  const hasSelectedTags = currentSelectedTags.length > 0;

  // Check scroll position
  const checkScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  useEffect(() => {
    checkScroll();
    window.addEventListener('resize', checkScroll);
    return () => window.removeEventListener('resize', checkScroll);
  }, []);

  const scroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const scrollAmount = 150;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  return (
    <div className="bg-gray-50 border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5">
        <div className="relative">
          {/* Left scroll button */}
          {canScrollLeft && (
            <button
              onClick={() => scroll('left')}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-7 h-7 flex items-center justify-center bg-white border border-gray-200 rounded-full shadow-sm hover:shadow-md transition-shadow"
              aria-label="Nach links scrollen"
            >
              <ChevronLeft className="w-3.5 h-3.5 text-gray-600" />
            </button>
          )}

          <div
            ref={scrollRef}
            onScroll={checkScroll}
            className="flex items-center gap-2 overflow-x-auto no-scrollbar"
          >
            {/* Clear All Button - nur anzeigen wenn Tags ausgewählt */}
            {hasSelectedTags && onClearAll && (
              <button
                onClick={onClearAll}
                className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 bg-white border border-gray-300 rounded-full transition-colors whitespace-nowrap flex-shrink-0"
              >
                <X className="w-3.5 h-3.5" />
                <span>Zurücksetzen</span>
              </button>
            )}

            {/* Vibe Tags (dynamisch je nach Realm) */}
            {tags.map((tag) => {
              const IconComponent = iconMap[tag.icon];
              const isSelected = (currentSelectedTags as string[]).includes(tag.id);

              const handleClick = () => {
                if (isGoodWork && onGoodworkTagToggle) {
                  onGoodworkTagToggle(tag.id as GoodWorkVibeTag);
                } else {
                  onTagToggle(tag.id as VibeTag);
                }
              };

              return (
                <button
                  key={tag.id}
                  onClick={handleClick}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap flex-shrink-0 ${
                    isSelected
                      ? isGoodWork
                        ? 'bg-indigo-700 text-white'
                        : 'bg-gray-900 text-white'
                      : 'bg-white text-gray-700 border border-gray-300 hover:border-gray-900'
                  }`}
                >
                  {IconComponent && <IconComponent className="w-4 h-4" />}
                  <span>{tag.label}</span>
                </button>
              );
            })}
          </div>

          {/* Right scroll button */}
          {canScrollRight && (
            <button
              onClick={() => scroll('right')}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-7 h-7 flex items-center justify-center bg-white border border-gray-200 rounded-full shadow-sm hover:shadow-md transition-shadow"
              aria-label="Nach rechts scrollen"
            >
              <ChevronRight className="w-3.5 h-3.5 text-gray-600" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default VibeTagFilter;
